import { Box, useTheme } from '@mui/material'
import React, { memo, useCallback, useContext, useMemo } from 'react'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../../custom'
import { useDispatch, useSelector } from 'react-redux'
import { AppContext } from '../../../../App'
import {
    CallPutType,
    MultiStrikeChartTypes,
    MultiStrikeSecuritiesClassType,
    setMultiStrikeNearAtm,
    setMultiStrikeSecurities,
    setMultiStrikeShowCallPut,
} from '../../../../reducer/multiStrikeChart.reducer'
import { updateMultiStrikeChartConfig } from '../../../../reducer/chart.reducer'
import { ChartViewKey } from '../../../../assets/constants'
import { selectMultiStrikeChartConfigSecurities } from '../../../../selectors/chart'
import CloseIcon from '@mui/icons-material/Close'
import { selectMultiStrikeChartSecurities } from '../../../../selectors/multiStrikeChart'
import store from '../../../../store'
import LiveText from '../../../common/LiveText'
import {
    buildMultiStrikeMetadataSubscribe,
    getMultiStrikeOutlookSelectors,
    getMultiStrikeValueSelector,
} from './multiStrikeMetadataLiveDisplay'
import { selectMultiStrikeStatusLineSettings } from '../../../../selectors/chart'
import {
    getMultiStrikeMetadataTableRowSx,
    MULTI_STRIKE_METADATA_ACTIONS_CLASS,
    MULTI_STRIKE_METADATA_ACTIONS_SX,
    MULTI_STRIKE_METADATA_LABEL_CELL_SX,
    MULTI_STRIKE_METADATA_OUTLOOK_CELL_SX,
    MULTI_STRIKE_METADATA_ROW_CLASS,
    MULTI_STRIKE_METADATA_STRIKE_CELL_SX,
    MULTI_STRIKE_METADATA_VALUE_CELL_SX,
} from './multiStrikeMetadataLayout'

export type SeriesMetadataProps = {
    strike: string
    callSeriesColor: string | undefined
    putSeriesColor: string | undefined
    callKey: string | undefined
    putKey: string | undefined
    callPut: CallPutType[]
    chartType: MultiStrikeChartTypes
    viewKey: string
}

const getMultiStrikeSecuritiesFromStore = (viewKey: string, isWidget: boolean) => {
    const state = store.getState()
    return isWidget
        ? selectMultiStrikeChartConfigSecurities(viewKey)(state)
        : selectMultiStrikeChartSecurities(state)
}

const SeriesOptionMetadataDisplay: React.FC<SeriesMetadataProps> = ({
    strike,
    putSeriesColor,
    callSeriesColor,
    callKey,
    putKey,
    callPut,
    chartType,
    viewKey,
}) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const isWidget = useMemo(() => viewKey !== ChartViewKey.MULTI_STRIKES_CHART, [viewKey])
    const statusLineSettings = useSelector(selectMultiStrikeStatusLineSettings(viewKey))
    const appContext = useContext(AppContext)
    const getChart = useCallback(() => appContext.getMultiStrikeChart(viewKey), [appContext, viewKey])
    const showStrikeColumn = !!strike

    const callSubscribe = useMemo(
        () => buildMultiStrikeMetadataSubscribe(viewKey, callKey ? [callKey] : []),
        [viewKey, callKey]
    )
    const putSubscribe = useMemo(
        () => buildMultiStrikeMetadataSubscribe(viewKey, putKey ? [putKey] : []),
        [viewKey, putKey]
    )
    const callSelectText = useMemo(
        () => (callKey ? getMultiStrikeValueSelector(viewKey, callKey, chartType) : () => '-'),
        [viewKey, callKey, chartType]
    )
    const putSelectText = useMemo(
        () => (putKey ? getMultiStrikeValueSelector(viewKey, putKey, chartType) : () => '-'),
        [viewKey, putKey, chartType]
    )
    const callOutlookSelectors = useMemo(
        () => getMultiStrikeOutlookSelectors(viewKey, callKey, getChart),
        [viewKey, callKey, getChart]
    )
    const putOutlookSelectors = useMemo(
        () => getMultiStrikeOutlookSelectors(viewKey, putKey, getChart),
        [viewKey, putKey, getChart]
    )

    const removeStrike = useCallback(() => {
        const chart = appContext.getMultiStrikeChart(viewKey)
        if (chart) {
            const multiStrikeChartSecurities = getMultiStrikeSecuritiesFromStore(viewKey, isWidget)
            const newMultiStrikeChartSecurities = { ...multiStrikeChartSecurities }
            chart.dataManager.removeStrike(strike)
            delete newMultiStrikeChartSecurities[strike]
            if (isWidget) {
                dispatch(
                    updateMultiStrikeChartConfig({
                        viewKey,
                        config: {
                            securities: newMultiStrikeChartSecurities,
                            nearAtm: undefined,
                            atm: undefined,
                        },
                    })
                )
            } else {
                dispatch(setMultiStrikeSecurities(newMultiStrikeChartSecurities))
                dispatch(
                    setMultiStrikeNearAtm({
                        nearAtm: undefined,
                        atm: undefined,
                    })
                )
            }
        }
    }, [appContext, dispatch, isWidget, strike, viewKey])

    const handleCumOiCallPutChange = useCallback(
        (isCall: boolean) => {
            let updatedCallPut = [...(callPut ?? ['CE', 'PE'])]
            const valueToToggle = isCall ? 'CE' : 'PE'
            if (updatedCallPut.includes(valueToToggle)) {
                updatedCallPut = updatedCallPut.filter((item) => item !== valueToToggle)
            } else {
                updatedCallPut.push(valueToToggle)
            }
            if (isWidget) {
                dispatch(updateMultiStrikeChartConfig({ viewKey, config: { callPut: updatedCallPut } }))
            } else {
                dispatch(setMultiStrikeShowCallPut(updatedCallPut))
            }
        },
        [callPut, dispatch, isWidget, viewKey]
    )

    const handleCallPutChange = useCallback(
        (isCall: boolean) => {
            const chart = appContext.getMultiStrikeChart(viewKey)
            const multiStrikeChartSecurities = getMultiStrikeSecuritiesFromStore(viewKey, isWidget)
            const multiStrikeClassType = chart?.secondarySecurities ?? {}
            const updatedSecurities = { ...multiStrikeChartSecurities }
            if (updatedSecurities[strike]) {
                let security = updatedSecurities[strike]
                const toggleType = isCall ? 'CE' : 'PE'
                if (security.includes(toggleType)) {
                    security = security.filter((item) => item !== toggleType)
                } else {
                    security = [...security, toggleType]
                }
                updatedSecurities[strike] = security

                const callPutSecurities: MultiStrikeSecuritiesClassType = {}
                Object.keys(updatedSecurities).forEach((strikeKey) => {
                    callPutSecurities[strikeKey] = {
                        callPut: updatedSecurities[strikeKey],
                        callSecKey: multiStrikeClassType[strikeKey]?.callSecKey,
                        putSecKey: multiStrikeClassType[strikeKey]?.putSecKey,
                    }
                })

                if (isWidget) {
                    dispatch(updateMultiStrikeChartConfig({ viewKey, config: { securities: updatedSecurities } }))
                } else {
                    dispatch(setMultiStrikeSecurities(updatedSecurities))
                }
                chart?.updateStrikeCallPutVisible(callPutSecurities)
            }
        },
        [appContext, dispatch, isWidget, strike, viewKey]
    )

    const toggleCallPut = chartType === 'cumOi' ? handleCumOiCallPutChange : handleCallPutChange

    const showStrikeMetadata =
        (statusLineSettings.indicatorTitle ?? true) && (statusLineSettings.indicatorValues ?? true)

    const renderSide = useCallback(
        (
            side: 'CE' | 'PE',
            seriesColor: string | undefined,
            seriesKey: string | undefined,
            subscribe: (listener: () => void) => () => void,
            selectText: () => string,
            outlookSelectors: { selectText: () => string; selectColor: () => string | undefined }
        ) => {
            const isCall = side === 'CE'
            const isVisible = callPut?.includes(side)
            const isPeOutlook = side === 'PE'
            return (
                <>
                    <Box
                        component="td"
                        sx={{
                            ...MULTI_STRIKE_METADATA_LABEL_CELL_SX,
                            textDecoration: isVisible ? 'none' : 'line-through',
                        }}
                    >
                        <NonInteractiveTooltip placement="top" title={isVisible ? 'hide' : 'show'} arrow={isCall}>
                            <Box component="span" onClick={() => toggleCallPut(isCall)}>
                                {side}
                            </Box>
                        </NonInteractiveTooltip>
                    </Box>
                    <Box
                        component="td"
                        sx={{
                            ...MULTI_STRIKE_METADATA_VALUE_CELL_SX,
                            color: seriesColor ?? theme.palette.text.primary,
                            opacity: isVisible ? 1 : 0.3,
                        }}
                    >
                        {isVisible && seriesKey ? (
                            <LiveText subscribe={subscribe} selectText={selectText} />
                        ) : (
                            '-'
                        )}
                    </Box>
                    <Box
                        component="td"
                        sx={{
                            ...MULTI_STRIKE_METADATA_OUTLOOK_CELL_SX,
                            color: seriesColor ?? theme.palette.text.primary,
                            opacity: isVisible ? 1 : 0.3,
                            ...(isPeOutlook ? { paddingRight: showStrikeColumn ? '20px' : `${8}px` } : {}),
                        }}
                    >
                        {isVisible && seriesKey ? (
                            <LiveText
                                subscribe={subscribe}
                                selectText={outlookSelectors.selectText}
                                selectColor={outlookSelectors.selectColor}
                            />
                        ) : (
                            '-'
                        )}
                        {isPeOutlook && showStrikeColumn ? (
                            <Box
                                className={MULTI_STRIKE_METADATA_ACTIONS_CLASS}
                                sx={{ ...MULTI_STRIKE_METADATA_ACTIONS_SX, color: 'text.secondary' }}
                            >
                                <NonInteractiveTooltip placement={'top'} title={'Remove'} arrow>
                                    <ExtraSmallButton
                                        color="inherit"
                                        size="small"
                                        onClick={removeStrike}
                                        data-event={'Multi strike chat remove series clicked'}
                                        data-event-series-name={`${callKey ?? ''} ${putKey ?? ''}`}
                                        sx={{ height: '18px', width: '18px', minWidth: '18px', p: 0, color: 'inherit' }}
                                    >
                                        <CloseIcon sx={{ fontSize: '16px' }} />
                                    </ExtraSmallButton>
                                </NonInteractiveTooltip>
                            </Box>
                        ) : null}
                    </Box>
                </>
            )
        },
        [callPut, callKey, putKey, removeStrike, showStrikeColumn, theme.palette.text.primary, toggleCallPut]
    )

    if (!showStrikeMetadata) {
        return null
    }

    return (
        <Box
            component="tr"
            className={MULTI_STRIKE_METADATA_ROW_CLASS}
            sx={{ color: 'text.secondary', ...getMultiStrikeMetadataTableRowSx(theme) }}
        >
            {showStrikeColumn ? (
                <Box component="td" sx={MULTI_STRIKE_METADATA_STRIKE_CELL_SX}>
                    {strike}
                </Box>
            ) : null}
            {renderSide('CE', callSeriesColor, callKey, callSubscribe, callSelectText, callOutlookSelectors)}
            {renderSide('PE', putSeriesColor, putKey, putSubscribe, putSelectText, putOutlookSelectors)}
        </Box>
    )
}

export default memo(SeriesOptionMetadataDisplay, (prev, next) => {
    const prevCallPut = prev.callPut?.join(',') ?? ''
    const nextCallPut = next.callPut?.join(',') ?? ''
    return (
        prev.viewKey === next.viewKey &&
        prev.strike === next.strike &&
        prev.callKey === next.callKey &&
        prev.putKey === next.putKey &&
        prev.chartType === next.chartType &&
        prev.callSeriesColor === next.callSeriesColor &&
        prev.putSeriesColor === next.putSeriesColor &&
        prevCallPut === nextCallPut
    )
})
